import { type DiscoverChatbotProfileOverlayProps, setIsOverlayLogin, useAppDispatch } from "@next-platform/client-common-core";
import { useCallback, useMemo } from "react";

import { ROUTES } from "@/constants";
import useLocalizedNavigation from "@/hooks/useLocalizedNavigation";
import useDiscoverFavorite from "@/pages/Discover/hooks/useDiscoverFavorite";

type UseDiscoverChatbotProfileOverlayActionsOptions = {
  readonly openShareModal: (characterId: string) => void;
  readonly openBlockModal: (creatorUserId: string) => void;
  readonly onLessLikeThis: () => void;
  readonly onClose: () => void;
};

export type DiscoverChatbotProfileOverlayActions = Pick<
  DiscoverChatbotProfileOverlayProps,
  "onChatNow" | "onFavorite" | "onShare" | "onCreatorPress" | "onLorebookPress" | "onBlockCreator" | "onReport" | "onLessLikeThis"
>;

/** Web behaviour of the overlay CTAs. The overlay stays open for most actions (chat, favorite, creator, lorebook, report, less-like-this); it only closes when opening the share or block modals (or the login overlay on unauthenticated favorite). */
export default function useDiscoverChatbotProfileOverlayActions({
  openShareModal,
  openBlockModal,
  onLessLikeThis,
  onClose,
}: UseDiscoverChatbotProfileOverlayActionsOptions): DiscoverChatbotProfileOverlayActions {
  const dispatch = useAppDispatch();
  const localizedNavigate = useLocalizedNavigation();

  const onChatNow = useCallback(
    (characterId: string) => {
      localizedNavigate(`/chat/${characterId}`);
    },
    [localizedNavigate],
  );

  // The overlay closes first so the login overlay is the only sheet on screen.
  const onGuestFavorite = useCallback(() => {
    onClose();
    dispatch(setIsOverlayLogin(true));
  }, [dispatch, onClose]);
  // The overlay flips the heart while this settles and falls back to the store afterwards.
  const onFavorite = useDiscoverFavorite({ onGuest: onGuestFavorite });

  const onShare = useCallback(
    (characterId: string) => {
      onClose();
      openShareModal(characterId);
    },
    [onClose, openShareModal],
  );

  const onBlockCreator = useCallback(
    (creatorUserId: string) => {
      onClose();
      openBlockModal(creatorUserId);
    },
    [onClose, openBlockModal],
  );

  const onCreatorPress = useCallback(
    (creatorUsername: string) => {
      localizedNavigate(`${ROUTES.creator}/${creatorUsername}`);
    },
    [localizedNavigate],
  );

  const onLorebookPress = useCallback(
    (lorebookId: string) => {
      localizedNavigate(ROUTES.lorebookDetail.replace(":id", lorebookId));
    },
    [localizedNavigate],
  );
  const onReport = useCallback(
    (characterId: string) => {
      localizedNavigate(`${ROUTES.report}/${characterId}`);
    },
    [localizedNavigate],
  );

  return useMemo(
    () => ({ onChatNow, onFavorite, onShare, onBlockCreator, onCreatorPress, onLessLikeThis, onReport, onLorebookPress }),
    [onChatNow, onCreatorPress, onLessLikeThis, onReport, onFavorite, onLorebookPress, onShare, onBlockCreator],
  );
}
